
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone } from 'lucide-react';

const socialIcons = [
  { 
    name: 'X', 
    href: 'https://x.com/LawandcodeAfro', 
    icon: (
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
      </svg>
    ) 
  },
  { 
    name: 'LinkedIn', 
    href: 'https://www.linkedin.com/in/lawandcodeafrica', 
    icon: (
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ) 
  },
  { 
    name: 'Phone', 
    href: 'tel:+254731797354', 
    icon: <Phone size={20} />
  },
  { 
    name: 'WhatsApp', 
    href: 'https://wa.me/254731797354', 
    icon: (
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488"/>
      </svg>
    ) 
  }
];

const Footer = () => {
  const navigate = useNavigate();

  const handleNavClick = (to: string) => {
    navigate(to);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and About */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <Link to="/" className="text-2xl font-bold text-white hover:text-secondary transition-colors leading-none inline-block">
                Law <span className="font-serif italic font-normal text-secondary text-3xl align-middle">&amp;</span> Code <span className="text-blue-200">Africa</span>
              </Link>
              <div className="text-xs text-blue-200 tracking-[0.25em] uppercase mt-1">Law and Tech Commentary</div>
            </div>
            <p className="text-blue-100 leading-relaxed mb-6">
              Lawandcodeafrica.com is a leading AI law commentary dedicated to providing 
              expert guidance on AI principles, statutory provisions, policies, and their 
              interaction with IP, corporate governance, commercial and cyber-related issues.
            </p>
            <div className="flex gap-4">
              {socialIcons.map(s => (
                <a 
                  href={s.href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  key={s.name} 
                  className="text-blue-200 hover:text-secondary transition-colors p-2 rounded-full hover:bg-blue-800"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-secondary">Quick Links</h3>
            <ul className="space-y-2">
              <li><button onClick={() => handleNavClick('/about-us')} className="text-blue-200 hover:text-white transition-colors text-left">About Us</button></li>
              <li><button onClick={() => handleNavClick('/founder-profile')} className="text-blue-200 hover:text-white transition-colors text-left">Founder Profile</button></li>
              <li><button onClick={() => handleNavClick('/mentorship')} className="text-blue-200 hover:text-white transition-colors text-left">Mentorship</button></li>
              <li><button onClick={() => handleNavClick('/brochures')} className="text-blue-200 hover:text-white transition-colors text-left">Brochures and Publications</button></li>
              <li><button onClick={() => handleNavClick('/arbitration-adr')} className="text-blue-200 hover:text-white transition-colors text-left">Arbitration & ADR</button></li>
              <li><button onClick={() => handleNavClick('/contact')} className="text-blue-200 hover:text-white transition-colors text-left">Contact</button></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-secondary">Contact</h3>
            <div className="space-y-3 text-blue-200">
              <div>
                <div className="font-medium mb-1">Email</div>
                <div>lawandcodeafrica@gmail.com</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-blue-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-blue-200 text-sm">
            © {new Date().getFullYear()} Lawandcodeafrica.com. All rights reserved.
          </div>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy" className="text-blue-200 hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
